// One task card on the board: grip + rank (queue lanes), ticket key, title,
// severity or priority, blockers, round, PR, time in column, live run line
// and the card menu. Dragging is native HTML5; the board owns the state.

import { useEffect, useRef, useState, type DragEvent } from 'react'
import { Link } from '@tanstack/react-router'
import type { BoardCard as BoardCardData, StreamDefinition } from '@goblic/shared'
import { formatMoney } from '../../lib/format'
import { LiveDuration } from '../live-duration'
import { SEVERITY_CLASS, isDraggable, timeInColumn } from './board-utils'
import { RoleAvatar } from '../goblin-art'
import { useIllustrations } from '../../lib/illustrations'
import { SubtaskCardSummary } from '../tasks/subtasks'

export interface BoardCardProps {
  card: BoardCardData
  stream: StreamDefinition
  /** Streams a task can be moved to (the current one left out). */
  otherStreams: StreamDefinition[]
  /** 1-based position in a queue lane; null outside the lanes. */
  rankNumber: number | null
  dragging: boolean
  busy: boolean
  onDragStart: (card: BoardCardData) => void
  onDragEnd: () => void
  /** The pointer is over this card; `upperHalf` picks the slot above or below it. */
  onDragOverCard: (card: BoardCardData, upperHalf: boolean) => void
  onOpen: (card: BoardCardData) => void
  onStartNow: (card: BoardCardData) => void
  onMoveStream: (card: BoardCardData, streamKey: string) => void
  onUnpin: (card: BoardCardData) => void
  onArchive: (card: BoardCardData) => void
}

export function BoardCard({
  card,
  stream,
  otherStreams,
  rankNumber,
  dragging,
  busy,
  onDragStart,
  onDragEnd,
  onDragOverCard,
  onOpen,
  onStartNow,
  onMoveStream,
  onUnpin,
  onArchive,
}: BoardCardProps) {
  const [illustrations] = useIllustrations()
  const canDrag = isDraggable(card) && !busy
  const inLane = card.columnId.startsWith('lane:')
  const blocked = card.status === 'blocked'
  const failedChecks = card.readiness && !card.readiness.passed ? card.readiness.failed : []

  function handleDragStart(event: DragEvent<HTMLElement>): void {
    event.dataTransfer.effectAllowed = 'move'
    // Firefox needs data to start a drag.
    event.dataTransfer.setData('text/plain', card.id)
    onDragStart(card)
  }

  function handleDragOver(event: DragEvent<HTMLElement>): void {
    const rect = event.currentTarget.getBoundingClientRect()
    onDragOverCard(card, event.clientY < rect.top + rect.height / 2)
  }

  const priorityLabel = stream.order === 'priority_age' ? `P${card.priority}` : null
  const severityLabel =
    card.severity ?? (stream.order === 'severity_age' || stream.expedite ? 'S?' : null)

  return (
    <li
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
      aria-label={card.ticketKey ? `${card.ticketKey} ${card.title}` : card.title}
      className={`group relative rounded-control border bg-surface p-2.5 shadow-sm ${
        blocked ? 'border-danger' : card.waitingOnHuman ? 'border-signal' : 'border-line'
      } ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-start gap-1.5">
        {inLane ? (
          <span className="mt-0.5 flex shrink-0 items-center gap-1 text-ink-3" aria-hidden>
            <span className="select-none text-xs leading-none">⋮⋮</span>
            {rankNumber != null ? <span className="text-xs tabular-nums">{rankNumber}</span> : null}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs">
            {card.ticketKey ? <span className="font-mono text-ink-3">{card.ticketKey}</span> : null}
            {severityLabel ? (
              <span
                className={`rounded-full px-1.5 font-semibold ${
                  card.severity ? SEVERITY_CLASS[card.severity] : 'bg-sunken text-ink-3'
                }`}
                title={card.severity ? `Severity ${card.severity}` : 'No severity yet'}
              >
                {severityLabel}
              </span>
            ) : null}
            {priorityLabel ? (
              <span className="rounded-full bg-sunken px-1.5 font-semibold text-ink-2" title={`Priority ${card.priority}`}>
                {priorityLabel}
              </span>
            ) : null}
            {card.pinned ? (
              <span className="rounded-full bg-info-soft px-1.5 text-info" title="Pinned in place by hand">
                pinned
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onOpen(card)}
            className="mt-0.5 block w-full text-left text-base font-medium text-ink hover:underline"
          >
            {card.title}
          </button>
        </div>
        <CardMenu
          card={card}
          stream={stream}
          otherStreams={otherStreams}
          busy={busy}
          onOpen={onOpen}
          onStartNow={onStartNow}
          onMoveStream={onMoveStream}
          onUnpin={onUnpin}
          onArchive={onArchive}
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3">
        {card.blockedByCount > 0 ? (
          <span
            className="text-danger"
            title={`Blocked by ${card.blockedBy.map((b) => `${b.ticketKey ?? b.title}`).join(', ')}${
              card.blockedByCount > card.blockedBy.length ? ` and ${card.blockedByCount - card.blockedBy.length} more` : ''
            }`}
          >
            ⛓ {card.blockedByCount}
          </span>
        ) : null}
        {card.round != null && card.maxRounds != null ? (
          <span title="Round in this step">
            round {card.round}/{card.maxRounds}
          </span>
        ) : null}
        {card.pullRequestUrl ? (
          <a
            href={card.pullRequestUrl}
            target="_blank"
            rel="noreferrer"
            className="link text-xs"
            onClick={(event) => event.stopPropagation()}
          >
            PR #{card.pullRequestUrl.split('/').pop()}
          </a>
        ) : null}
        {card.outcome ? (
          <span
            className={`rounded-full px-1.5 font-medium ${
              card.outcome.outcome === 'success'
                ? 'bg-ok-soft text-ok'
                : card.outcome.outcome === 'failure'
                  ? 'bg-danger-soft text-danger'
                  : 'bg-sunken text-ink-2'
            }`}
          >
            {card.outcome.state}
          </span>
        ) : null}
        {card.waitingOnHuman ? (
          <span className="rounded-full bg-signal-soft px-1.5 font-semibold text-signal">your turn</span>
        ) : null}
        {blocked ? <span className="rounded-full bg-danger-soft px-1.5 font-semibold text-danger">blocked</span> : null}
        {card.columnId !== 'done' ? (
          <span className="ml-auto" title="Time in this column">
            {timeInColumn(card.enteredColumnAt)}
          </span>
        ) : null}
      </div>

      <SubtaskCardSummary summary={card.subtaskSummary} />

      {card.activeRun ? (
        <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-xs text-ink-2">
          {illustrations ? (
            <RoleAvatar roleName={card.activeRun.roleName} size={18} />
          ) : (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-info" aria-hidden />
          )}
          <Link to="/runs/$runId" params={{ runId: card.activeRun.id }} className="hover:underline">
            {card.activeRun.roleName ?? card.activeRun.agentName ?? (card.activeRun.kind === 'decision' ? 'Decision' : 'Agent')}
          </Link>
          <span>·</span>
          <LiveDuration startedAt={card.activeRun.startedAt} />
          <span>·</span>
          <span className="tabular-nums">{formatMoney(card.activeRun.costCents)}</span>
        </p>
      ) : null}

      {card.queueReason ? (
        <p className="mt-1.5 rounded-control bg-signal-soft px-2 py-1 text-xs text-signal">{card.queueReason}</p>
      ) : null}
      {failedChecks.length > 0 ? (
        <p className="mt-1.5 flex flex-wrap gap-1 text-xs">
          {failedChecks.map((check) => (
            <span key={check} className="rounded-full bg-signal-soft px-1.5 text-signal">
              {check === 'acceptance_criteria' ? 'needs criteria' : check === 'no_open_blockers' ? 'has blockers' : 'needs severity'}
            </span>
          ))}
        </p>
      ) : null}
    </li>
  )
}

function CardMenu({
  card,
  stream,
  otherStreams,
  busy,
  onOpen,
  onStartNow,
  onMoveStream,
  onUnpin,
  onArchive,
}: Pick<
  BoardCardProps,
  'card' | 'stream' | 'otherStreams' | 'busy' | 'onOpen' | 'onStartNow' | 'onMoveStream' | 'onUnpin' | 'onArchive'
>) {
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => {
    setOpen(false)
    setPicking(false)
  }
  const inLane = card.columnId.startsWith('lane:')
  const finished = card.columnId === 'done'
  const canUnpin = stream.order !== 'manual' && card.pinned
  const item = 'block w-full px-3 py-1.5 text-left text-base text-ink hover:bg-sunken disabled:opacity-45'

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        className="btn-ghost btn-sm !h-6 !px-1.5"
        aria-label="Card menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-7 z-20 w-48 overflow-hidden rounded-control border border-line-strong bg-surface py-1 shadow-overlay"
        >
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              close()
              onOpen(card)
            }}
          >
            Open
          </button>
          {inLane ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              disabled={busy}
              onClick={() => {
                close()
                onStartNow(card)
              }}
            >
              Start now
            </button>
          ) : null}
          {!finished && otherStreams.length > 0 ? (
            picking ? (
              <div className="border-t border-line">
                <p className="px-3 pt-1.5 text-xs text-ink-3">Move to stream</p>
                {otherStreams.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="menuitem"
                    className={item}
                    disabled={busy}
                    onClick={() => {
                      close()
                      onMoveStream(card, s.key)
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            ) : (
              <button type="button" role="menuitem" className={item} onClick={() => setPicking(true)}>
                Move to stream…
              </button>
            )
          ) : null}
          {finished ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              disabled={busy}
              onClick={() => {
                close()
                onArchive(card)
              }}
            >
              Archive
            </button>
          ) : null}
          {canUnpin ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              disabled={busy}
              onClick={() => {
                close()
                onUnpin(card)
              }}
            >
              Unpin
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
